import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { ControlProperty, TruckSize } from '../../../../../../common';
import { LocalDateRangeModel } from '../../../../../models';
import { AllocationViewActionAttachmentsForm } from '../allocation-attachments.model';
import { SealPhotosModel } from '../allocation-seal-picture-control';
import { sealPictureControlValidatorForTruckSealData } from '../allocation-seal-picture-control/allocation-seal-picture-control.validators';
import { AllocationViewSealForm, AllocationViewSecurityModel } from '../allocation-security';
import { AllocationViewActionCarrierForm } from '../allocation-view-action-carrier.model';
import {
  AllocationViewActionQuestionForm,
  AllocationViewActionQuestionModel,
} from '../allocation-view-action-question.model';
import { AllocationViewActionRequestUliModel } from '../allocation-view-action-request.model';
import {
  AllocationViewLoanedEquipmentViewForm,
  AllocationViewLoanedEquipmentViewModel,
} from '../allocation-view-loaned-equipment.model';

export class AllocationViewFinishDepartureSecurityModel extends AllocationViewSecurityModel {
  compartmentNames: string[];
  dangerousGoods: boolean;
  dockName: string;
  freeTextCarrierName: string;
  manufacturer: string;
  model: string;
  phoneNumber: string;
  returnLoanerPhone: boolean;
  returnVisitorBadge: boolean;
  securityQuestions: AllocationViewFinishDepartureSecurityQuestionsModel;
  truckPlate: string;
  truckPlateCountry: string;
  truckSize: TruckSize;
  visitorBadge: string;
  webLoanedEquipmentView?: AllocationViewLoanedEquipmentViewModel;
  sealCheckData: SealPhotosModel;
  needSealing: boolean;
}

export class AllocationViewFinishDepartureSecurityQuestionsModel {
  [key: string]: AllocationViewActionQuestionModel[];
}

export class AllocationViewFinishDepartureSecurityForm {
  constructor(
    model: AllocationViewFinishDepartureSecurityModel,
    fb: UntypedFormBuilder,
    dualSealCheckEnabled: boolean,
    public generalInformation: UntypedFormGroup = fb.group(
      new AllocationViewFinishDepartureSecurityGeneralInformationForm(model, fb),
    ),
    public securityQuestions: UntypedFormGroup = fb.group(
      new AllocationViewFinishDepartureSecurityQuestionsForm(model, fb),
    ),
    public sealCheck: UntypedFormGroup = fb.group(new AllocationViewFinishDepartureSecuritySealCheckForm(model, fb), {
      validators: dualSealCheckEnabled && model.needSealing ? sealPictureControlValidatorForTruckSealData : [],
    }),
    public attachments: UntypedFormGroup = fb.group(
      new AllocationViewFinishDepartureSecurityAttachmentsForm(model, fb),
    ),
  ) {}
}

export class AllocationViewFinishDepartureSecurityGeneralInformationForm {
  constructor(
    model: AllocationViewFinishDepartureSecurityModel,
    fb: UntypedFormBuilder,
    public truckPlate: ControlProperty<string> = model.truckPlate,
    public truckPlateCountry: ControlProperty<string> = model.truckPlateCountry,
    public truckSize: ControlProperty<TruckSize> = model.truckSize,
    public carrier: UntypedFormGroup = fb.group(new AllocationViewActionCarrierForm(model.carrier)),
    public freeTextCarrierName: ControlProperty<string> = [model.freeTextCarrierName, []],
    public serviceTime: ControlProperty<LocalDateRangeModel> = [model.serviceTime, []],
    public firstName: ControlProperty<string> = [model.firstName, []],
    public lastName: ControlProperty<string> = [model.lastName, []],
    public compartmentNames: ControlProperty<string[]> = [model.compartmentNames, []],
    public dangerousGoods: ControlProperty<boolean> = model.dangerousGoods,
    public propertyOfAnother: ControlProperty<boolean> = [model.propertyOfAnother, []],
    public remainingDrivingTime: ControlProperty<number> = [model.remainingDrivingTime, []],
    public hubId: ControlProperty<number> = [model.hubId, []],
    public truckId: ControlProperty<number> = [model.truckId, []],
    public ulis: ControlProperty<AllocationViewActionRequestUliModel[]> = [model.ulis, []],
  ) {}
}

export class AllocationViewFinishDepartureSecurityQuestionsForm {
  constructor(model: AllocationViewFinishDepartureSecurityModel, fb: UntypedFormBuilder) {
    const keys = Object.keys(model.securityQuestions).sort();

    keys.forEach((key: string, index: number) => {
      const isLast = keys.length - 1 === index;

      this[key] = fb.group(new AllocationViewFinishDepartureSecurityQuestionForm(model, fb, key, isLast));
    });
  }
}

export class AllocationViewFinishDepartureSecurityQuestionForm {
  securityCheckPassed: UntypedFormControl;

  constructor(
    model: AllocationViewFinishDepartureSecurityModel,
    fb: UntypedFormBuilder,
    key: string,
    isLast: boolean,
    public questions: UntypedFormArray = fb.array([]),
  ) {
    model.securityQuestions[key].forEach((question: AllocationViewActionQuestionModel) => {
      questions.push(fb.group(new AllocationViewActionQuestionForm(question)));
    });

    if (isLast) {
      this.securityCheckPassed = fb.control(null, [Validators.required]);
    }
  }
}

export class AllocationViewFinishDepartureSecuritySealCheckForm {
  constructor(
    model: AllocationViewFinishDepartureSecurityModel,
    fb: UntypedFormBuilder,
    public truckSealData: UntypedFormGroup = fb.group(new AllocationViewSealForm(model.sealCheckData?.truckSealData)),
    public trailerSealData: UntypedFormGroup = fb.group(
      new AllocationViewSealForm(model.sealCheckData?.trailerSealData),
    ),
    public truckSize: ControlProperty<TruckSize> = model.truckSize,
  ) {}
}

export class AllocationViewFinishDepartureSecurityAttachmentsForm {
  public webLoanedEquipmentView: UntypedFormGroup;

  constructor(
    model: AllocationViewFinishDepartureSecurityModel,
    fb: UntypedFormBuilder,
    public comments: UntypedFormGroup = fb.group(new AllocationViewActionAttachmentsForm(model.comments, fb)),
  ) {
    if (model.webLoanedEquipmentView) {
      this.webLoanedEquipmentView = fb.group(
        new AllocationViewLoanedEquipmentViewForm(model?.webLoanedEquipmentView, fb),
      );
    }
  }
}
